<template>
  <div>
    <div class="col-md-12 card text-left">
      <div class="card-header"></div>
      <div class="report-filters">
        <div class="col-md-3 col-sm-6 col-xs-12 form-group pull-left">
          <input type="text" :placeholder="$t('reports.searchClient')" class="form-control" v-model="filters.searchClient">
        </div>

        <div class="col-md-3 col-sm-6 col-xs-12 form-group pull-left">
          <select class="form-control" v-model="filters.campaignId">
            <option :value="null">{{ $t('donationCampaigns.allCampaigns') }}</option>
            <option v-for="c in campaigns" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>

        <div class="col-md-3 col-sm-6 col-xs-12 form-group pull-left date-range">
          <el-date-picker
            type="daterange"
            v-model="filters.valid"
            class="mid"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            :name="pickerNames"
            :picker-options="pickerOptions">
          </el-date-picker>
        </div>

        <div class="search-button-reports col-md-1 col-sm-6 col-xs-12 form-group pull-left">
          <div :title="$t('reports.search')" class="btn btn-icon btn-info btn-search" @click="getData(null)">
            <i class="ti-search"></i>
          </div>
        </div>

        <div class="search-button-reports col-md-1 col-sm-6 col-xs-12 form-group pull-left">
          <div :title="$t('reports.export')" class="btn btn-icon btn-info btn-print" @click="exportData()">
            <i class="ti-file"></i>
          </div>
        </div>
      </div>

      <!-- TOTALS -->
      <div v-if="totals" class="row mar-to-15 mar-bt-15" style="padding: 0 15px;">
        <div class="col-md-4">
          <div class="card" style="padding: 15px; text-align: center;">
            <h5 style="margin: 0;">{{ $t('donationReport.totalAmount') }}</h5>
            <h3 style="margin: 5px 0 0 0;">{{ parseFloat(totals.amount || 0).toFixed(2) }} {{ currency }}</h3>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card" style="padding: 15px; text-align: center;">
            <h5 style="margin: 0;">{{ $t('donationReport.totalDonations') }}</h5>
            <h3 style="margin: 5px 0 0 0;">{{ totals.donations || 0 }}</h3>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card" style="padding: 15px; text-align: center;">
            <h5 style="margin: 0;">{{ $t('donationReport.totalDonors') }}</h5>
            <h3 style="margin: 5px 0 0 0;">{{ totals.donors || 0 }}</h3>
          </div>
        </div>
      </div>

      <div class="card-content total-pagination pad-15">
        <data-tables-server
          @query-change="paginationChanged"
          v-loading="loading"
          :data="tableData"
          :search-def="{ show: false }"
          :total="total"
          :pagination-props="paginationProps"
          :table-props="tableOptions"
          style="width: 100%;">

          <el-table-column :min-width="60" prop="id" :label="$t('reports.id')" sortable="custom"></el-table-column>

          <el-table-column :min-width="100" prop="campaign_name" :label="$t('donationReport.campaign')" sortable="custom">
            <template slot-scope="props">
              {{ (props.row.campaign && props.row.campaign.name) || props.row.campaign_name || '-' }}
            </template>
          </el-table-column>

          <el-table-column :min-width="100" :label="$t('donationReport.client')">
            <template slot-scope="props">
              <template v-if="props.row.user">
                {{ props.row.user.firstname }} {{ props.row.user.lastname }}
              </template>
              <template v-else>
                {{ props.row.client_name || '-' }}
              </template>
            </template>
          </el-table-column>

          <el-table-column :min-width="120" :label="$t('reports.email')">
            <template slot-scope="props">
              {{ (props.row.user && props.row.user.email) || props.row.client_email || '-' }}
            </template>
          </el-table-column>

          <el-table-column :min-width="80" :label="$t('reports.phone')">
            <template slot-scope="props">
              {{ (props.row.user && props.row.user.phone) || props.row.client_phone || '-' }}
            </template>
          </el-table-column>

          <el-table-column :min-width="90" prop="amount" :label="$t('donationReport.amount')" sortable="custom">
            <template slot-scope="props">
              <b>{{ parseFloat(props.row.amount || 0).toFixed(2) }} {{ currency }}</b>
            </template>
          </el-table-column>

          <el-table-column :min-width="80" prop="order_id" :label="$t('reports.orderId')" sortable="custom">
            <template slot-scope="props">
              {{ props.row.order_id || '-' }}
            </template>
          </el-table-column>

          <el-table-column :min-width="100" prop="location_name" :label="$t('reports.location')">
            <template slot-scope="props">
              {{ (props.row.location && props.row.location.name) || props.row.location_name || '-' }}
            </template>
          </el-table-column>

          <el-table-column :min-width="100" prop="created_at" :label="$t('donationReport.date')" sortable="custom">
            <template slot-scope="props">
              {{ formatDateTime(props.row.created_at) }}
            </template>
          </el-table-column>
        </data-tables-server>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { DatePicker } from 'element-ui'
import swal from 'sweetalert2'

export default {
  components: {
    [DatePicker.name]: DatePicker
  },
  props: {
    isDonationReport: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    isDonationReport () {
      if (this.isDonationReport) {
        this.getData()
      }
    }
  },
  created () {
    this.getCampaigns()
    this.getData()
  },
  data () {
    return {
      loading: false,
      currency: this.getCurrencySymbol(this.$session.get('selectedCompanyData')['currency_iso']),
      filters: {
        valid: [
          moment().date(1).format('YYYY-MM-DD'),
          moment().endOf('month').format('YYYY-MM-DD')
        ],
        searchClient: null,
        campaignId: null
      },
      paginationProps: {
        layout: 'prev, pager, next, sizes, total'
      },
      tableOptions: {
        defaultSort: {
          prop: 'created_at',
          order: 'descending'
        }
      },
      tableData: [],
      campaigns: [],
      total: 0,
      totals: null,
      sortBy: 'created_at',
      sortOrder: 'desc',
      pickerOptions: {
        disabledDate (date) {
          return date < moment().subtract(12, 'months')
        }
      },
      pickerNames: ['date_from', 'date_to']
    }
  },
  methods: {
    getCampaigns () {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest('/reports/donation-campaigns', 'GET', data, (response) => {
        if (response.status === true) {
          if (response.data && response.data.data) {
            this.campaigns = response.data.data
          } else {
            this.campaigns = response.data || []
          }
        }
      })
    },
    paginationChanged (query) {
      if (query.type === 'page' || query.type === 'size') {
        this.getData(query)
      }
      if (query.type === 'sort') {
        if (query.sort.order) {
          this.sortBy = query.sort.prop
          this.sortOrder = query.sort.order === 'descending' ? 'desc' : 'asc'
        } else {
          this.sortBy = 'created_at'
          this.sortOrder = 'desc'
        }
        this.getData(query)
      }
    },
    getData (query) {
      if (!this.isDonationReport) {
        return
      }
      let sortField = this.sortBy
      let sortType = this.sortOrder
      let pageSize = 20
      let page = 1

      if (query) {
        if (query.sort && query.sort.prop) {
          sortField = query.sort.prop
        }
        if (query.sort && query.sort.order) {
          sortType = query.sort.order === 'descending' ? 'desc' : 'asc'
        }
        if (query.pageSize) {
          pageSize = query.pageSize
        }
        if (query.page) {
          page = query.page
        }
      }

      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        start_date: this.filters.valid && this.filters.valid[0],
        end_date: this.filters.valid && this.filters.valid[1],
        search_client: this.filters.searchClient,
        campaign_id: this.filters.campaignId,
        field: sortField,
        order: sortType
      }

      this.loading = true
      this.apiRequest('/reports/donations?page=' + page + '&pageSize=' + pageSize, 'GET', data, (response) => {
        if (response.status === true) {
          this.tableData = response.data.data || response.data.items || []
          this.total = response.data.total || 0
          if (response.data.totals) {
            this.totals = response.data.totals
          }
        }
        this.$nextTick(() => {
          this.loading = false
        })
      })
    },
    exportData () {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        start_date: this.filters.valid && this.filters.valid[0],
        end_date: this.filters.valid && this.filters.valid[1],
        search_client: this.filters.searchClient,
        campaign_id: this.filters.campaignId
      }
      this.loading = true
      this.apiRequest('/reports/donations-export', 'GET', data, (response) => {
        if (response.status === true) {
          const link = document.createElement('a')
          link.href = response.data.file
          link.setAttribute('download', response.data.file_name)
          document.body.appendChild(link)
          link.click()
        } else {
          swal('', this.$t('validations.wentWrong'), 'error')
        }
        this.loading = false
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Reports/DonationReport.vue